// src/pages/Hub/components/Globe.js
//
// Holographic 3D operations globe (react-three-fiber + drei + three — all already in
// the project, no new deps). A deep-ocean earth with the REAL world map: glowing
// coastlines drawn from bundled Natural Earth 110m land outlines (one merged
// LineSegments draw call), a faint LED dot-fill over the landmasses, a refined
// lat/lng graticule, and a Fresnel atmosphere rim shaded in screen space so the limb
// glows as you orbit. Region-coloured pulsing pins are placed from real lat/lng, with
// faint arcs between region centroids. Auto-rotates; drag to orbit, scroll to zoom.
// Clicking a pin bubbles the point up so the hub can open its detail drawer.

import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars, Line } from '@react-three/drei';
import * as THREE from 'three';
import { regionMeta } from '../theme';
import landRings from '../data/land110m.json'; // [ [ [lng,lat], ... ], ... ]

const R = 1;

function latLngToVec3(lat, lng, r = R) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(r * Math.sin(phi) * Math.cos(theta)),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

// ── Coastlines: every land ring → screen of line segments, one buffer / one draw call.
function useCoastlineGeometry() {
  return useMemo(() => {
    const positions = [];
    const rA = R * 1.005;
    for (const ring of landRings) {
      for (let i = 0; i < ring.length - 1; i += 1) {
        const a = latLngToVec3(ring[i][1], ring[i][0], rA);
        const b = latLngToVec3(ring[i + 1][1], ring[i + 1][0], rA);
        positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, []);
}

// ── Land dot-fill: coarse lat/lng grid, point-in-polygon against the land mask
// (bbox-prefiltered), spacing corrected by cos(lat) so dots stay roughly even.
function useLandDotGeometry() {
  return useMemo(() => {
    const boxes = landRings.map((ring) => {
      let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
      for (const [x, y] of ring) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
      return [minX, minY, maxX, maxY];
    });
    const inRing = (lng, lat, ring) => {
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const xi = ring[i][0]; const yi = ring[i][1];
        const xj = ring[j][0]; const yj = ring[j][1];
        if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    };
    const isLand = (lng, lat) => {
      for (let k = 0; k < landRings.length; k += 1) {
        const b = boxes[k];
        if (lng < b[0] || lng > b[2] || lat < b[1] || lat > b[3]) continue;
        if (inRing(lng, lat, landRings[k])) return true;
      }
      return false;
    };
    const positions = [];
    const rA = R * 1.004;
    const step = 2.4;
    for (let lat = -78; lat <= 84; lat += step) {
      const lngStep = step / Math.max(0.25, Math.cos((lat * Math.PI) / 180));
      for (let lng = -180; lng < 180; lng += lngStep) {
        if (isLand(lng, lat)) {
          const v = latLngToVec3(lat, lng, rA);
          positions.push(v.x, v.y, v.z);
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, []);
}

// ── Fresnel atmosphere: back-side shell, glow keyed to the screen-space normal so the
// rim lights up as the camera orbits (classic three.js halo).
function useAtmosphereMaterial(color, power, edge) {
  return useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      glowColor: { value: new THREE.Color(color) },
      p: { value: power },
      c: { value: edge },
    },
    vertexShader: `
      varying vec3 vNormal;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform vec3 glowColor;
      uniform float p;
      uniform float c;
      varying vec3 vNormal;
      void main() {
        float intensity = clamp(pow(c - dot(vNormal, vec3(0.0, 0.0, 1.0)), p), 0.0, 1.0);
        gl_FragColor = vec4(glowColor, 1.0) * intensity;
      }`,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    transparent: true,
    depthWrite: false,
  }), [color, power, edge]);
}

function GlobeBody() {
  const coastGeo = useCoastlineGeometry();
  const dotGeo = useLandDotGeometry();
  const atmoOuter = useAtmosphereMaterial('#38BDF8', 2.4, 0.62);
  const atmoInner = useAtmosphereMaterial('#6366F1', 3.4, 1.0);

  return (
    <group>
      {/* deep-ocean core */}
      <mesh>
        <sphereGeometry args={[R, 64, 64]} />
        <meshStandardMaterial color="#0a1828" emissive="#05111f" emissiveIntensity={0.6} roughness={0.85} metalness={0.18} />
      </mesh>

      {/* lat/lng graticule */}
      <mesh scale={1.002}>
        <sphereGeometry args={[R, 36, 24]} />
        <meshBasicMaterial color="#173352" wireframe transparent opacity={0.2} />
      </mesh>

      {/* land dot-fill */}
      <points geometry={dotGeo}>
        <pointsMaterial color="#7fe3ff" size={0.011} sizeAttenuation transparent opacity={0.5} depthWrite={false} toneMapped={false} />
      </points>

      {/* glowing coastlines (real world map) */}
      <lineSegments geometry={coastGeo}>
        <lineBasicMaterial color="#63d0ff" transparent opacity={0.6} depthWrite={false} toneMapped={false} />
      </lineSegments>

      {/* atmosphere rim glow */}
      <mesh scale={1.17} material={atmoOuter}>
        <sphereGeometry args={[R, 48, 48]} />
      </mesh>
      <mesh scale={1.03} material={atmoInner}>
        <sphereGeometry args={[R, 48, 48]} />
      </mesh>
    </group>
  );
}

function Pin({ position, color, onClick }) {
  const pulse = useRef();
  useFrame((state) => {
    if (!pulse.current) return;
    const t = (state.clock.elapsedTime * 0.9) % 1; // 0..1 loop
    pulse.current.scale.setScalar(1 + t * 3.4);
    if (pulse.current.material) pulse.current.material.opacity = 0.5 * (1 - t);
  });
  return (
    <group position={position}>
      <mesh onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}>
        <sphereGeometry args={[0.014, 14, 14]} />
        <meshBasicMaterial color={color} toneMapped={false} />
      </mesh>
      <mesh ref={pulse}>
        <sphereGeometry args={[0.017, 12, 12]} />
        <meshBasicMaterial color={color} transparent opacity={0.45} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

function Arc({ a, b, color }) {
  const points = useMemo(() => {
    const mid = a.clone().add(b).multiplyScalar(0.5).normalize().multiplyScalar(R * 1.45);
    return new THREE.QuadraticBezierCurve3(a, mid, b).getPoints(48);
  }, [a, b]);
  return <Line points={points} color={color} lineWidth={1} transparent opacity={0.35} />;
}

export default function Globe({ points = [], onSelectPoint }) {
  const valid = useMemo(
    () => points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)).slice(0, 250),
    [points],
  );

  const pins = useMemo(
    () => valid.map((p) => ({ p, vec: latLngToVec3(p.lat, p.lng, R * 1.012), color: regionMeta(p.region).color })),
    [valid],
  );

  // One centroid per region (projected back to the surface), arcs drawn pairwise.
  const arcs = useMemo(() => {
    const byRegion = {};
    valid.forEach((p) => {
      (byRegion[p.region] = byRegion[p.region] || []).push(latLngToVec3(p.lat, p.lng, R));
    });
    const centroids = Object.entries(byRegion).map(([region, vs]) => {
      const c = vs.reduce((acc, v) => acc.add(v), new THREE.Vector3()).multiplyScalar(1 / vs.length).normalize().multiplyScalar(R * 1.01);
      return { region, c };
    });
    const out = [];
    for (let i = 0; i < centroids.length; i += 1) {
      for (let j = i + 1; j < centroids.length; j += 1) {
        out.push({ a: centroids[i].c, b: centroids[j].c, color: '#38BDF8', key: `${centroids[i].region}-${centroids[j].region}` });
      }
    }
    return out;
  }, [valid]);

  return (
    <Canvas camera={{ position: [0, 0.4, 2.6], fov: 42 }} dpr={[1, 2]} gl={{ antialias: true, alpha: true }}>
      <ambientLight intensity={0.55} />
      <pointLight position={[4, 2, 3]} intensity={1.4} color="#bfe8ff" />
      <pointLight position={[-3, -2, -2]} intensity={0.3} color="#6366F1" />
      <Stars radius={60} depth={32} count={2800} factor={3.2} fade speed={0.6} />
      <GlobeBody />
      {arcs.map((arc) => <Arc key={arc.key} a={arc.a} b={arc.b} color={arc.color} />)}
      {pins.map(({ p, vec, color }) => (
        <Pin key={`${p.region}-${p.id}`} position={vec} color={color} onClick={() => onSelectPoint && onSelectPoint(p)} />
      ))}
      <OrbitControls enablePan={false} enableZoom minDistance={1.7} maxDistance={4.5} autoRotate autoRotateSpeed={0.45} rotateSpeed={0.6} />
    </Canvas>
  );
}
